/**
 * Date utilities with timezone support
 */

// Default timezone (fallback) — updated at login via setDefaultTimezone
let _defaultTimezone = 'Asia/Jerusalem';

export const setDefaultTimezone = (tz: string): void => {
  if (tz) {
    _defaultTimezone = tz;
  }
};

export const getDefaultTimezone = (): string => _defaultTimezone;

/**
 * Parse a date string as UTC.
 * The API returns naive datetime strings (no 'Z' suffix) that are stored as UTC.
 * JavaScript treats bare ISO datetime strings as local time, so we append 'Z' to
 * force UTC interpretation and avoid a double-offset bug.
 * Date-only strings (YYYY-MM-DD) are already treated as UTC by JS — no change needed.
 */
/**
 * Parse a CLINIC-LOCAL WALL CLOCK datetime - what meetings.scheduled_at holds.
 *
 * Not the same as parseDateAsUTC, and the difference is load-bearing. An
 * appointment's stored "09:00" IS nine in the morning where the clinic is;
 * appending a Z and converting shifts it by the clinic's offset, which is
 * zero in GMT and an hour in BST - so the bug would appear in spring and
 * look like the calendar breaking by itself.
 *
 * The returned Date carries those parts in the BROWSER's zone, which is what
 * the calendar wants: it asks for hour and minute to position a block, and
 * the answer should be the clinic's wall clock, unconverted.
 */
export const parseWallClock = (date: string): Date => {
  if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(date)) {
    const [d, t] = date.replace(' ', 'T').split('T');
    const [y, mo, da] = d.split('-').map(Number);
    const [h, mi, se] = t.replace('Z', '').split(':').map(Number);
    return new Date(y, mo - 1, da, h, mi, se || 0);
  }
  return new Date(date);
};

export const parseDateAsUTC = (date: string): Date => {
  // Match datetime strings like "2026-03-12T13:00:00" or "2026-03-12 13:00:00"
  // eslint-disable-next-line no-useless-escape
  if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(date) && !date.endsWith('Z') && !/[+\-]\d{2}:\d{2}$/.test(date)) {
    return new Date(date.replace(' ', 'T') + 'Z');
  }
  return new Date(date);
};

/**
 * Get date/time parts in the specified timezone using the Intl API.
 * This avoids the double-offset bug that occurs when using Date.getHours() etc.
 * on a Date that was manually shifted by a timezone offset — getHours() always
 * reads the *browser's local* timezone, not the one you shifted to.
 */
const getPartsInTimezone = (date: Date, timezone: string): Record<string, string> => {
  const opts: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  };
  const parts = new Intl.DateTimeFormat('en-GB', opts).formatToParts(date);
  const result: Record<string, string> = {};
  parts.forEach(({ type, value }) => {
    result[type] = value;
  });
  // Some environments emit "24" for midnight with hour12:false — normalise it
  if (result.hour === '24') result.hour = '00';
  return result;
};

/**
 * Format date and time in specified timezone or user's local timezone
 * @param date Date string or Date object
 * @param options Intl.DateTimeFormatOptions
 * @param timezone IANA timezone name (default: 'Asia/Jerusalem') or false for local time
 * @returns Formatted date string
 */
/**
 * Format a CLINIC-LOCAL WALL CLOCK value - meetings.scheduled_at.
 *
 * Passing one to formatDate/formatDateTime treats it as UTC and converts it
 * to a timezone, which shifts it by that zone's offset and, near either end
 * of a day, into the neighbouring one. A meeting at 11:00 on the 30th then
 * reads as the 31st for anyone east of the clinic.
 *
 * There is nothing to convert here: the stored parts ARE the clinic's.
 */
/**
 * A wall-clock value for a datetime-local input, and back.
 *
 * Both are near-identity on purpose. The stored parts are already the
 * clinic's, and the input shows and returns those same parts - converting
 * either way would move the meeting by the viewer's offset, and editing
 * anything on the form would silently reschedule it.
 */
export const wallClockToInput = (value: string | null | undefined): string =>
  (value || '').replace(' ', 'T').slice(0, 16);

export const inputToWallClock = (value: string | null | undefined): string => {
  const v = (value || '').replace(' ', 'T').slice(0, 16);
  return v ? `${v}:00` : '';
};

export const formatWallClockDate = (date: string | Date): string => {
  const d = typeof date === 'string' ? parseWallClock(date) : date;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
};

/** The clock time of a wall-clock value, as HH:mm. */
export const formatWallClockTime = (date: string | Date): string => {
  const d = typeof date === 'string' ? parseWallClock(date) : date;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
};

export const formatWallClockDateTime = (date: string | Date): string => {
  const d = typeof date === 'string' ? parseWallClock(date) : date;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${formatWallClockDate(d)} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

export const formatDateTime = (
  date: string | Date,
  options: Intl.DateTimeFormatOptions = {},
  timezone: string | false = _defaultTimezone
): string => {
  const dateObj = typeof date === 'string' ? parseDateAsUTC(date) : date;

  const hasTimeOptions = options.hour !== undefined || options.minute !== undefined;
  const hasDateOptions = options.year !== undefined || options.month !== undefined || options.day !== undefined;

  if (timezone) {
    try {
      const p = getPartsInTimezone(dateObj, timezone);
      if (hasDateOptions && !hasTimeOptions) {
        return `${p.day}/${p.month}/${p.year}`;
      } else if (!hasDateOptions && hasTimeOptions) {
        return `${p.hour}:${p.minute}`;
      } else {
        return `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}`;
      }
    } catch {
      // fall through to local-time fallback
    }
  }

  // Fallback: browser local time
  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear();
  const hours = dateObj.getHours().toString().padStart(2, '0');
  const minutes = dateObj.getMinutes().toString().padStart(2, '0');

  if (hasDateOptions && !hasTimeOptions) return `${day}/${month}/${year}`;
  if (!hasDateOptions && hasTimeOptions) return `${hours}:${minutes}`;
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};


/**
 * Format date only (no time) in specified timezone or user's local timezone
 * @param date Date string or Date object
 * @param timezone IANA timezone name (default: 'Asia/Jerusalem') or false for local time
 * @returns Formatted date string
 */
export const formatDate = (
  date: string | Date,
  timezone: string | false = _defaultTimezone
): string => {
  const dateObj = typeof date === 'string' ? parseDateAsUTC(date) : date;

  if (timezone) {
    try {
      const p = getPartsInTimezone(dateObj, timezone);
      return `${p.day}/${p.month}/${p.year}`;
    } catch {
      // fall through
    }
  }

  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Age in whole years, by calendar comparison.
 *
 * Not a days/365.25 division: that approximation drifts and can read a year
 * early around a birthday. Returns null when there is no usable date of birth,
 * so callers can render a placeholder instead of "NaN".
 *
 * @param dateOfBirth Date-only string ("YYYY-MM-DD") or Date object
 * @returns Age in years, or null
 */
export const calculateAge = (dateOfBirth: string | Date | null | undefined): number | null => {
  if (!dateOfBirth) return null;

  let birthYear: number;
  let birthMonth: number; // 1-12
  let birthDay: number;

  if (typeof dateOfBirth === 'string') {
    // Date-only strings parse as UTC midnight, and reading them back with the
    // local getters shifts the day west of Greenwich. Take the parts verbatim.
    const parts = dateOfBirth.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!parts) return null;
    birthYear = Number(parts[1]);
    birthMonth = Number(parts[2]);
    birthDay = Number(parts[3]);
  } else {
    birthYear = dateOfBirth.getFullYear();
    birthMonth = dateOfBirth.getMonth() + 1;
    birthDay = dateOfBirth.getDate();
  }

  const today = new Date();
  const todayMonth = today.getMonth() + 1;
  const todayDay = today.getDate();

  let age = today.getFullYear() - birthYear;
  if (todayMonth < birthMonth || (todayMonth === birthMonth && todayDay < birthDay)) {
    age--;
  }
  return age;
};

/**
 * Format time only (no date) in specified timezone or user's local timezone
 * @param date Date string or Date object
 * @param timezone IANA timezone name (default: 'Asia/Jerusalem') or false for local time
 * @returns Formatted time string
 */
export const formatTime = (
  date: string | Date,
  timezone: string | false = _defaultTimezone
): string => {
  const dateObj = typeof date === 'string' ? parseDateAsUTC(date) : date;

  if (timezone) {
    try {
      const p = getPartsInTimezone(dateObj, timezone);
      return `${p.hour}:${p.minute}`;
    } catch {
      // fall through
    }
  }

  const hours = dateObj.getHours().toString().padStart(2, '0');
  const minutes = dateObj.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

/**
 * Format date and time for meeting display
 * @param date Date string or Date object
 * @param timezone IANA timezone name (default: 'Asia/Jerusalem') or false for local time
 * @returns Formatted date and time string
 */
export const formatMeetingDateTime = (
  date: string | Date,
  timezone: string | false = _defaultTimezone
): string => {
  const dateObj = typeof date === 'string' ? parseDateAsUTC(date) : date;

  if (timezone) {
    try {
      const p = getPartsInTimezone(dateObj, timezone);
      return `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}`;
    } catch {
      // fall through
    }
  }

  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear();
  const hours = dateObj.getHours().toString().padStart(2, '0');
  const minutes = dateObj.getMinutes().toString().padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

/**
 * Get timezone display name
 * @param timezone IANA timezone name (default: 'Asia/Jerusalem') or false for local time
 * @returns Timezone abbreviation or name
 */
export const getTimezoneDisplay = (timezone: string | false = _defaultTimezone): string => {
  if (!timezone) {
    return 'Local Time';
  }

  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en', {
      timeZone: timezone,
      timeZoneName: 'short',
    });
    const parts = formatter.formatToParts(now);
    const timeZoneName = parts.find(part => part.type === 'timeZoneName');
    return timeZoneName?.value || timezone;
  } catch {
    return timezone;
  }
};

/**
 * Convert a datetime input (assumed to be in specified timezone) to UTC
 * @param datetimeLocal DateTime string from datetime-local input (YYYY-MM-DDTHH:mm)
 * @param timezone IANA timezone name (default: 'Asia/Jerusalem')
 * @returns UTC date string suitable for API
 */
export const convertTimezoneInputToUTC = (datetimeLocal: string, timezone: string = _defaultTimezone): string => {
  if (!datetimeLocal) return '';

  // Treat input as UTC to create a reference Date
  const refDate = new Date(datetimeLocal + ':00Z');

  // Find what refDate looks like in the target timezone
  const tzParts = getPartsInTimezone(refDate, timezone);

  // Reconstruct the tz-displayed time as a UTC timestamp for arithmetic
  const tzDateTime = new Date(
    `${tzParts.year}-${tzParts.month}-${tzParts.day}T${tzParts.hour}:${tzParts.minute}:00Z`
  );

  // offset = refDate(UTC) − tz-display(UTC) = −(TZ offset from UTC)
  const offset = refDate.getTime() - tzDateTime.getTime();

  // Actual UTC time = local-time-treated-as-UTC + offset
  const utcDate = new Date(refDate.getTime() + offset);
  return utcDate.toISOString();
};

/**
 * Backward compatibility: Convert Jerusalem timezone input to UTC
 * @deprecated Use convertTimezoneInputToUTC instead
 */
export const convertJerusalemInputToUTC = (datetimeLocal: string): string => {
  return convertTimezoneInputToUTC(datetimeLocal, _defaultTimezone);
};

/**
 * Convert a UTC datetime to specified timezone for datetime-local input
 * @param utcDateString UTC datetime string
 * @param timezone IANA timezone name (default: 'Asia/Jerusalem')
 * @returns Local datetime string in YYYY-MM-DDTHH:mm format for datetime-local input
 */
export const convertUTCToTimezoneInput = (utcDateString: string, timezone: string = _defaultTimezone): string => {
  if (!utcDateString) return '';
  const utcDate = parseDateAsUTC(utcDateString);
  const p = getPartsInTimezone(utcDate, timezone);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
};

/**
 * Backward compatibility: Convert UTC to Jerusalem timezone for input
 * @deprecated Use convertUTCToTimezoneInput instead
 */
export const convertUTCToJerusalemInput = (utcDateString: string): string => {
  return convertUTCToTimezoneInput(utcDateString, _defaultTimezone);
};

/**
 * Null-safe wrapper around formatDate. Returns fallback string for null/undefined dates.
 */
export const formatDateSafe = (
  date: string | null | undefined,
  timezone: string | false = _defaultTimezone,
  fallback: string = '—'
): string => {
  if (!date) return fallback;
  return formatDate(date, timezone);
};

/**
 * Null-safe wrapper around formatDateTime. Returns fallback string for null/undefined dates.
 */
export const formatDateTimeSafe = (
  date: string | null | undefined,
  timezone: string | false = _defaultTimezone,
  fallback: string = '—'
): string => {
  if (!date) return fallback;
  return formatDateTime(date, {}, timezone);
};
